import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PoopDodgeGame.css';

const PoopDodgeGame = () => {
  const [gameState, setGameState] = useState('menu'); // 'menu', 'playing', 'paused', 'gameOver'
  const [playerX, setPlayerX] = useState(50); // 플레이어 X 위치 (0-100)
  const [poops, setPoops] = useState([]); // 똥 배열
  const [powerUps, setPowerUps] = useState([]); // 파워업 배열
  const [score, setScore] = useState(0); // 피한 똥 개수
  const [lives, setLives] = useState(3); // 생명력
  const [collision, setCollision] = useState(false); // 충돌 상태
  const [scoreAnimation, setScoreAnimation] = useState(false); // 점수 애니메이션
  const [gameTime, setGameTime] = useState(0); // 게임 시간
  const [level, setLevel] = useState(1); // 현재 레벨
  const [combo, setCombo] = useState(0); // 콤보
  const [highScore, setHighScore] = useState(0); // 최고 점수
  const [isInvincible, setIsInvincible] = useState(false); // 무적 상태
  const gameRef = useRef(null);
  const animationRef = useRef(null);
  const lastPoopTime = useRef(0);
  const lastPowerUpTime = useRef(0);
  const playerVelocityRef = useRef(0); // 플레이어 속도 (ref로 변경)
  const lastKeyPress = useRef({ left: 0, right: 0 });
  const gameStartTime = useRef(0);
  const isGameRunning = useRef(false); // 게임 실행 상태
  const lastFrameTime = useRef(0); // 마지막 프레임 시간

  // 게임 시작
  const startGame = useCallback(() => {
    setGameState('playing');
    setPlayerX(50);
    playerVelocityRef.current = 0; // 시작 시 정지 상태
    setPoops([]);
    setPowerUps([]);
    setScore(0);
    setLives(3);
    setGameTime(0);
    setLevel(1);
    setCombo(0);
    setIsInvincible(false);
    const now = performance.now(); // performance.now()로 통일
    lastPoopTime.current = now;
    lastPowerUpTime.current = now;
    lastKeyPress.current = { left: 0, right: 0 };
    gameStartTime.current = now;
    lastFrameTime.current = now;
    isGameRunning.current = true;
  }, []);

  // 게임 종료
  const gameOver = useCallback(() => {
    setGameState('gameOver');
    isGameRunning.current = false;
    // 최고 점수 업데이트
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('poopDodgeHighScore', score.toString());
    }
    // 모든 애니메이션 프레임 취소
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, [highScore, score]);

  // 일시정지
  const togglePause = useCallback(() => {
    if (gameState === 'playing') {
      setGameState('paused');
    } else if (gameState === 'paused') {
      setGameState('playing');
    }
  }, [gameState]);

  // 플레이어 이동
  const movePlayer = useCallback((direction) => {
    if (gameState !== 'playing') return;

    const now = Date.now();
    if (direction < 0) {
      lastKeyPress.current.left = now;
    } else if (direction > 0) {
      lastKeyPress.current.right = now;
    }

    // 방향에 따른 속도 조정 (한 번 누르면 계속 움직임)
    if (direction !== 0) {
      const acceleration = 0.02; // 가속도 조정
      const maxSpeed = 0.8; // 최대 속도 제한
      playerVelocityRef.current += direction * acceleration;
      playerVelocityRef.current = Math.max(-maxSpeed, Math.min(maxSpeed, playerVelocityRef.current));
    }
  }, [gameState]);

  // 키보드 이벤트 처리
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'a':
        case 'A':
          if (gameState === 'playing') movePlayer(-3);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          if (gameState === 'playing') movePlayer(3);
          break;
        case ' ':
          if (gameState === 'menu') startGame();
          else if (gameState === 'gameOver') setGameState('menu');
          else if (gameState === 'playing' || gameState === 'paused') togglePause();
          break;
        case 'Escape':
          if (gameState === 'playing' || gameState === 'paused') togglePause();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, movePlayer, startGame, togglePause]);

  // 난이도 계산
  const getDifficulty = useCallback(() => {
    const baseSpeed = 0.4; // 기본 속도 감소
    const speedIncrease = 0.05; // 속도 증가량 감소
    const baseSpawnRate = 2000; // 기본 생성 주기 증가
    const spawnRateDecrease = 80; // 생성 주기 감소량 감소

    return {
      speed: baseSpeed + (level - 1) * speedIncrease,
      spawnRate: Math.max(800, baseSpawnRate - (level - 1) * spawnRateDecrease)
    };
  }, [level]);

  // 레벨 업데이트 - gameLoop 내부에서 직접 처리로 미사용
  // eslint-disable-next-line no-unused-vars
  const updateLevel = useCallback(() => {
    const newLevel = Math.floor(gameTime / 30) + 1; // 30초마다 레벨업
    if (newLevel !== level) {
      setLevel(newLevel);
    }
  }, [gameTime, level]);

  // 기존 함수들 제거 - 새로운 게임 루프에서 모든 것을 처리

  // 파워업 활성화
  const activatePowerUp = useCallback((powerUp) => {
    switch (powerUp.type) {
      case 'shield':
        setIsInvincible(true);
        setTimeout(() => setIsInvincible(false), powerUp.duration);
        break;
      case 'life':
        setLives(l => Math.min(l + 1, 5)); // 최대 5개
        break;
      case 'slow':
        // 시간 감속 효과 (똥 속도 감소)
        break;
      default:
        break;
    }
  }, []);

  // 플레이어 물리 업데이트 - gameLoop 내부에서 직접 처리로 미사용
  // eslint-disable-next-line no-unused-vars
  const updatePlayerPhysics = useCallback(() => {
    if (gameState !== 'playing') return;

    const now = Date.now();
    const timeSinceLeft = now - lastKeyPress.current.left;
    const timeSinceRight = now - lastKeyPress.current.right;

    // 키를 누르지 않으면 천천히 감속
    if (timeSinceLeft > 150 && timeSinceRight > 150) {
      const friction = 0.95; // 마찰력 증가
      playerVelocityRef.current *= friction;

      // 속도가 너무 작아지면 완전히 멈춤
      if (Math.abs(playerVelocityRef.current) < 0.01) {
        playerVelocityRef.current = 0;
      }
    }

    // 속도에 따른 위치 업데이트
    setPlayerX(prev => {
      const newX = prev + playerVelocityRef.current;
      return Math.max(5, Math.min(95, newX)); // 화면 경계 제한
    });
  }, [gameState]);

  // 게임 시간 업데이트 - gameLoop 내부에서 직접 처리로 미사용
  // eslint-disable-next-line no-unused-vars
  const updateGameTime = useCallback(() => {
    if (gameState === 'playing') {
      const currentTime = Date.now();
      const elapsed = Math.floor((currentTime - gameStartTime.current) / 1000);
      setGameTime(elapsed);
    }
  }, [gameState]);

  // 완전히 새로운 게임 루프
  const gameLoop = useCallback((currentTime) => {
    if (!isGameRunning.current || gameState !== 'playing') {
      return;
    }

    // 프레임 시간 계산 - 현재 미사용
    // eslint-disable-next-line no-unused-vars
    const deltaTime = currentTime - lastFrameTime.current;
    lastFrameTime.current = currentTime;

    // 게임 시간 업데이트 (수정된 계산)
    const elapsed = Math.max(0, Math.floor((currentTime - gameStartTime.current) / 1000));
    setGameTime(elapsed);

    // 레벨 업데이트 (수정된 계산)
    const newLevel = Math.max(1, Math.floor(elapsed / 30) + 1);
    if (newLevel !== level) {
      setLevel(newLevel);
    }

    // 똥 생성 (수정된 시간 계산)
    const difficulty = getDifficulty();
    const timeSinceLastPoop = Math.max(0, currentTime - lastPoopTime.current);
    if (timeSinceLastPoop > difficulty.spawnRate) {
      const poopTypes = [
        { type: 'normal', speed: difficulty.speed, size: 1, points: 1 },
        { type: 'fast', speed: difficulty.speed * 1.5, size: 0.8, points: 2 },
        { type: 'slow', speed: difficulty.speed * 0.7, size: 1.2, points: 3 }
      ];

      const selectedType = poopTypes[Math.floor(Math.random() * poopTypes.length)];

      const newPoop = {
        id: currentTime + Math.random(),
        x: Math.random() * 80 + 10,
        y: -20,
        speed: selectedType.speed,
        type: selectedType.type,
        size: selectedType.size,
        points: selectedType.points
      };
      setPoops(prev => [...prev, newPoop]);
      lastPoopTime.current = currentTime;
    }

    // 파워업 생성 (수정된 시간 계산)
    const timeSinceLastPowerUp = Math.max(0, currentTime - lastPowerUpTime.current);
    if (timeSinceLastPowerUp > 10000) {
      const powerUpTypes = [
        { type: 'shield', duration: 5000, effect: '무적' },
        { type: 'life', duration: 0, effect: '생명+1' },
        { type: 'slow', duration: 3000, effect: '시간감속' }
      ];

      const selectedType = powerUpTypes[Math.floor(Math.random() * powerUpTypes.length)];

      const newPowerUp = {
        id: currentTime + Math.random(),
        x: Math.random() * 80 + 10,
        y: -20,
        speed: 0.3,
        type: selectedType.type,
        duration: selectedType.duration,
        effect: selectedType.effect
      };
      setPowerUps(prev => [...prev, newPowerUp]);
      lastPowerUpTime.current = currentTime;
    }

    // 똥 업데이트
    setPoops(prev => {
      const updated = prev.map(poop => ({
        ...poop,
        y: poop.y + poop.speed
      })).filter(poop => {
        if (poop.y > 100) {
          setScore(s => s + poop.points);
          setCombo(c => c + 1);
          setScoreAnimation(true);
          setTimeout(() => setScoreAnimation(false), 300);
          return false;
        }

        if (!isInvincible && poop.y >= 75 && poop.y <= 85) {
          const playerLeft = playerX - 3;
          const playerRight = playerX + 3;
          if (poop.x >= playerLeft && poop.x <= playerRight) {
            setCollision(true);
            setCombo(0);
            setTimeout(() => setCollision(false), 300);

            if (!isInvincible) {
              setLives(l => {
                const newLives = l - 1;
                if (newLives <= 0) {
                  setTimeout(gameOver, 100);
                }
                return newLives;
              });
            }
            return false;
          }
        }

        return true;
      });

      return updated;
    });

    // 파워업 업데이트
    setPowerUps(prev => {
      const updated = prev.map(powerUp => ({
        ...powerUp,
        y: powerUp.y + powerUp.speed
      })).filter(powerUp => {
        if (powerUp.y > 100) {
          return false;
        }

        if (powerUp.y >= 75 && powerUp.y <= 85) {
          const playerLeft = playerX - 3;
          const playerRight = playerX + 3;
          if (powerUp.x >= playerLeft && powerUp.x <= playerRight) {
            activatePowerUp(powerUp);
            return false;
          }
        }

        return true;
      });

      return updated;
    });

    // 플레이어 물리 업데이트 (수정된 시간 계산)
    const now = Date.now();
    const timeSinceLeft = Math.max(0, now - lastKeyPress.current.left);
    const timeSinceRight = Math.max(0, now - lastKeyPress.current.right);

    if (timeSinceLeft > 300 && timeSinceRight > 300) { // 감속 시작 시간 연장 (더 오래 움직임)
      const friction = 0.98; // 마찰력 감소 (더 천천히 감속)
      playerVelocityRef.current *= friction;

      if (Math.abs(playerVelocityRef.current) < 0.01) { // 완전 정지 임계값 증가
        playerVelocityRef.current = 0;
      }
    }

    setPlayerX(prev => {
      const newX = prev + playerVelocityRef.current;
      return Math.max(5, Math.min(95, newX));
    });

    // 다음 프레임 요청
    if (isGameRunning.current) {
      animationRef.current = requestAnimationFrame(gameLoop);
    }
  }, [gameState, level, playerX, isInvincible, getDifficulty, activatePowerUp, gameOver]);

  // 게임 루프 시작/정지
  useEffect(() => {
    if (gameState === 'playing') {
      isGameRunning.current = true;
      lastFrameTime.current = performance.now();
      gameStartTime.current = performance.now(); // 시작 시간 재설정
      animationRef.current = requestAnimationFrame(gameLoop);
    } else {
      isGameRunning.current = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      isGameRunning.current = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState, gameLoop]);

  // 최고 점수 로드
  useEffect(() => {
    const savedHighScore = localStorage.getItem('poopDodgeHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore));
    }
  }, []);

  // 렌더링
  const renderPlayer = () => (
    <div
      className={`player ${collision ? 'collision' : ''}`}
      style={{ left: `${playerX}%` }}
    >
      <svg width="30" height="30" viewBox="0 0 40 40" fill="none">
        {/* 픽셀아트 캐릭터 - 검은색만 사용 */}
        {/* 머리 */}
        <rect x="12" y="8" width="16" height="16" fill="#000" />

        {/* 눈 */}
        <rect x="15" y="12" width="2" height="2" fill="#fff" />
        <rect x="23" y="12" width="2" height="2" fill="#fff" />

        {/* 입 */}
        <rect x="18" y="16" width="4" height="2" fill="#fff" />

        {/* 몸통 */}
        <rect x="14" y="24" width="12" height="12" fill="#000" />

        {/* 팔 */}
        <rect x="10" y="26" width="4" height="8" fill="#000" />
        <rect x="26" y="26" width="4" height="8" fill="#000" />

        {/* 다리 */}
        <rect x="16" y="36" width="3" height="4" fill="#000" />
        <rect x="21" y="36" width="3" height="4" fill="#000" />

        {/* 손 */}
        <rect x="8" y="30" width="2" height="2" fill="#000" />
        <rect x="30" y="30" width="2" height="2" fill="#000" />

        {/* 발 */}
        <rect x="15" y="40" width="2" height="2" fill="#000" />
        <rect x="23" y="40" width="2" height="2" fill="#000" />
      </svg>
    </div>
  );

  const renderPoop = (poop) => (
    <div
      key={poop.id}
      className={`poop poop-${poop.type}`}
      style={{
        left: `${poop.x}%`,
        top: `${poop.y}%`,
        transform: `scale(${poop.size})`
      }}
    >
      <svg width="80" height="80" viewBox="0 0 40 40" fill="none" stroke="#000" strokeWidth="2">
        {/* 더 자연스러운 똥 모양 */}
        {/* 똥 꼭대기 (가장 작고 둥글게) */}
        <ellipse cx="20" cy="8" rx="4" ry="3" fill="none" />

        {/* 똥 중간 (점점 넓어지는 부분) */}
        <ellipse cx="20" cy="16" rx="8" ry="5" fill="none" />

        {/* 똥 하단 (더 넓은 부분) */}
        <ellipse cx="20" cy="24" rx="10" ry="6" fill="none" />

        {/* 똥 바닥 (가장 넓고 찌부러진 부분) */}
        <ellipse cx="20" cy="32" rx="12" ry="7" fill="none" />

        {/* 똥 내부 선들 (질감 표현) */}
        <line x1="16" y1="12" x2="24" y2="12" stroke="#000" />
        <line x1="14" y1="18" x2="26" y2="18" stroke="#000" />
        <line x1="12" y1="24" x2="28" y2="24" stroke="#000" />
        <line x1="10" y1="30" x2="30" y2="30" stroke="#000" />
      </svg>
    </div>
  );

  const renderPowerUp = (powerUp) => (
    <div
      key={powerUp.id}
      className={`powerup powerup-${powerUp.type}`}
      style={{
        left: `${powerUp.x}%`,
        top: `${powerUp.y}%`
      }}
    >
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#000" strokeWidth="2">
        {powerUp.type === 'shield' && (
          <circle cx="20" cy="20" r="15" fill="none" stroke="#0066ff" />
        )}
        {powerUp.type === 'life' && (
          <path d="M20 10 L25 20 L35 20 L27 27 L30 37 L20 30 L10 37 L13 27 L5 20 L15 20 Z" fill="#ff0000" />
        )}
        {powerUp.type === 'slow' && (
          <circle cx="20" cy="20" r="15" fill="none" stroke="#00ff00" />
        )}
      </svg>
    </div>
  );

  const renderMenu = () => (
    <div className="game-menu">
      <h1>💩 똥피하기 게임 💩</h1>
      <p>방향키 또는 A/D로 이동하세요</p>
      <p>스페이스바로 시작 | ESC로 일시정지</p>
      {highScore > 0 && <p className="high-score">최고 점수: {highScore}점</p>}
      <button onClick={startGame} className="start-btn">
        게임 시작
      </button>
    </div>
  );

  const renderGameOver = () => (
    <div className="game-over">
      <h1>게임 오버!</h1>
      <p>최종 점수: {score}점</p>
      <p>플레이 시간: {Math.floor(gameTime / 60)}:{(gameTime % 60).toString().padStart(2, '0')}</p>
      <p>최고 레벨: {level}</p>
      {score > highScore && <p className="new-record">🎉 새로운 기록! 🎉</p>}
      <button onClick={() => setGameState('menu')} className="restart-btn">
        다시 시작
      </button>
    </div>
  );

  const renderPause = () => (
    <div className="game-pause">
      <h2>일시정지</h2>
      <p>스페이스바 또는 ESC로 계속</p>
    </div>
  );

  return (
    <div className="poop-dodge-game" ref={gameRef}>
      {gameState === 'menu' && renderMenu()}

      {(gameState === 'playing' || gameState === 'paused') && (
        <div className="game-container">
          <div className="game-info">
            <span className={scoreAnimation ? 'score-increase' : ''}>점수: {score}</span>
            <span>생명: {'❤️'.repeat(lives)}</span>
            <span>레벨: {level}</span>
            <span>시간: {Math.floor(gameTime / 60)}:{(gameTime % 60).toString().padStart(2, '0')}</span>
            {combo > 0 && <span className="combo">콤보: {combo}</span>}
            {isInvincible && <span className="invincible">🛡️ 무적</span>}
          </div>

          <div className="game-area">
            {renderPlayer()}
            {poops.map(renderPoop)}
            {powerUps.map(renderPowerUp)}
          </div>

          <div className="game-controls">
            <p>← → 또는 A/D: 이동 | 스페이스바/ESC: 일시정지</p>
          </div>
        </div>
      )}

      {gameState === 'paused' && renderPause()}
      {gameState === 'gameOver' && renderGameOver()}
    </div>
  );
};

export default PoopDodgeGame;
